import { c as _c } from "react/compiler-runtime";
import { useProgressApiClient } from "@api/progress-api/use-progress-api-client";
import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useToast } from "@flat-ui";
import { backgroundTasksFetchingStatusSelector } from "@store/sdb-background-tasks/sdb-background-tasks-selector";
import { updateBackgroundTasks } from "@store/sdb-background-tasks/sdb-background-tasks-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { useCallback, useEffect, useRef } from "react";

/**
 * Default delta time in milliseconds used to poll the callback.
 * Trade-off between being up-to-date and browser + server resource usage.
 */
const DEFAULT_POLLING_INTERVAL = 30_000;

/** Default maximum number of failed attempts before the polling is stopped */
const DEFAULT_MAX_ATTEMPTS = 10;
/**
 * Requests background tasks to the progress API at a regular polling interval and updates the store.
 */
export function useBackgroundTasksTracker(t0) {
  const $ = _c(17);
  const {
    projectId,
    taskType
  } = t0;
  const isFirstFailedAttempt = useRef(true);
  const failedCounterRef = useRef(0);
  const pollingIntervalIdRef = useRef(undefined);
  let t1;
  if ($[0] !== projectId) {
    t1 = {
      projectId
    };
    $[0] = projectId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const progressApiClient = useProgressApiClient(t1);
  const dispatch = useAppDispatch();
  const backgroundTasksFetchingStatus = useAppSelector(backgroundTasksFetchingStatusSelector);
  let t2;
  if ($[2] !== projectId) {
    t2 = {
      projectId
    };
    $[2] = projectId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const projectApiClient = useProjectApiClient(t2);
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    openToast
  } = useToast();
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      clearInterval(pollingIntervalIdRef.current);
      pollingIntervalIdRef.current = undefined;
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const stopPolling = t3;
  let t4;
  if ($[5] !== dispatch || $[6] !== handleErrorWithToast || $[7] !== openToast || $[8] !== progressApiClient || $[9] !== projectApiClient || $[10] !== taskType) {
    t4 = async shouldOverrideBeforePage => {
      ;
      try {
        await dispatch(updateBackgroundTasks({
          progressApiClient,
          projectApiClient,
          beforePage: null,
          shouldOverrideBeforePage,
          taskType
        })).unwrap();
        failedCounterRef.current = 0;
      } catch (t5) {
        const error = t5;
        failedCounterRef.current = failedCounterRef.current + 1;
        if (isFirstFailedAttempt.current) {
          isFirstFailedAttempt.current = false;
          openToast({
            title: "Failed to fetch cloud activity tasks",
            variant: "error"
          });
        }
        if (failedCounterRef.current >= DEFAULT_MAX_ATTEMPTS) {
          stopPolling();
          handleErrorWithToast({
            id: `fetchBackgroundTasks-${Date.now().toString()}`,
            title: "Failed to fetch activity tasks after multiple attempts. Please reload the application to try again.",
            error
          });
        }
      }
    };
    $[5] = dispatch;
    $[6] = handleErrorWithToast;
    $[7] = openToast;
    $[8] = progressApiClient;
    $[9] = projectApiClient;
    $[10] = taskType;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const fetchBackgroundTasks = t4;
  let t5;
  let t6;
  if ($[12] !== fetchBackgroundTasks) {
    t5 = () => {
      if (!pollingIntervalIdRef.current) {
        fetchBackgroundTasks(true);
      }
      pollingIntervalIdRef.current = setInterval(() => fetchBackgroundTasks(false), DEFAULT_POLLING_INTERVAL);
      return () => stopPolling();
    };
    t6 = [fetchBackgroundTasks, stopPolling];
    $[12] = fetchBackgroundTasks;
    $[13] = t5;
    $[14] = t6;
  } else {
    t5 = $[13];
    t6 = $[14];
  }
  useEffect(t5, t6);
  const t7 = backgroundTasksFetchingStatus === "uninitialized" || backgroundTasksFetchingStatus === "pending";
  let t8;
  if ($[15] !== t7) {
    t8 = {
      isLoading: t7
    };
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}